import React, { useEffect, useContext, useState } from "react";
import {
  GridContainer,
  GridItem,
  FormRootArea,
} from "./PeriodicAcquirerReport.styles";
import {
  periodicAcquirerReportInitialValues,
  periodicAcquirerReportValidationSchema,
} from "./PeriodicAcquirerReport.resources";
import { useFormik } from "formik";
import TextField from "../../textField/TextField";
import DateField from "../../dateField/DateField";
import { Button, InputAdornment, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { generateAcquirerReport } from "./PeriodicAcquirerReport.thunk";
import AuthContext from "../../../context/AuthContext";
import CurrencySelectField from "../../selectField/CurrencySelectField";
import PeriodicAcquirerReportResultModal from "./PeriodicAcquirerReport.resultModal";
import moment from "moment";
import { useModal } from "../../modal";
import AdminFields from "../../adminFields/AdminFields";
import {
  setApplyChanges,
  setWasUsed,
  setReset,
  setIsoID,
} from "../../adminFields/AdminFields.slice";
import AdminFieldsModal from "../../adminFields/AdminFields.modal";
import ListIcon from "@mui/icons-material/List";

const PeriodicAcquirerReport = () => {
  const dispatch = useDispatch();
  const { token, acquirerData, userInfo } = useContext(AuthContext);
  const { currency_code: defaultCurrency, isoID: defaultIsoID } =
    acquirerData || {};
  const { open } = useModal();
  const { status } = useSelector((state) => state.periodicAcquirerReport);
  const {
    applyChanges,
    acquirerId,
    wasUsed,
    reset,
    acquirerCurrencyId,
    isoID: storeIsoID,
  } = useSelector((state) => state.adminFields);
  const [modalType, setModalType] = useState("iso");
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenResultModal = () => {
    open({
      title: `Date range (Acquirer ISO) Report from ${moment(
        values.reportStartDate,
      ).format("MM/DD/YYYY")} to ${moment(values.reportEndDate).format(
        "MM/DD/YYYY",
      )}`,
      maxWidth: "lg",
      fullWidth: true,
    });
  };

  const handleOpenModalISO = () => {
    setModalType("iso");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
  };

  const handleSelectISO = (selectedISO) => {
    dispatch(setIsoID(selectedISO.isoID));
    setFieldValue("isoID", selectedISO.isoID);
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleChange,
    handleBlur,
    errors,
    touched,
    setSubmitting,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: {
      ...periodicAcquirerReportInitialValues,
      isoID: defaultIsoID || periodicAcquirerReportInitialValues.isoID,
      currency: defaultCurrency || periodicAcquirerReportInitialValues.currency,
    },
    validationSchema: periodicAcquirerReportValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      setSubmitting(true);
      dispatch(
        generateAcquirerReport({
          isoID: wasUsed ? storeIsoID : values.isoID,
          currency: values.currency,
          reportStartDate: values.reportStartDate,
          reportEndDate: values.reportEndDate,
          token,
          acquirerData,
          onSuccessCallback: handleOpenResultModal,
          ...(wasUsed && { storeAcquirerId: acquirerId }),
        }),
      );
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (reset) {
      setFieldValue("isoID", defaultIsoID || "");
      setFieldValue("currency", defaultCurrency || "");
      dispatch(setReset(false));
    }
  }, [reset, defaultIsoID, defaultCurrency, setFieldValue, dispatch]);

  useEffect(() => {
    if (applyChanges) {
      setFieldValue("isoID", storeIsoID || "");
      setFieldValue("currency", acquirerCurrencyId || "");
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(true));
    }
  }, [applyChanges, storeIsoID, acquirerCurrencyId, setFieldValue, dispatch]);

  useEffect(() => {
    if (wasUsed && storeIsoID === "" && acquirerId === "") {
      dispatch(setApplyChanges(false));
      dispatch(setWasUsed(false));
      setFieldValue("isoID", defaultIsoID || "");
      setFieldValue("currency", defaultCurrency || "");
    }
  }, [
    wasUsed,
    storeIsoID,
    acquirerId,
    defaultIsoID,
    defaultCurrency,
    setFieldValue,
    dispatch,
  ]);

  useEffect(() => {
    if (
      defaultCurrency &&
      !wasUsed &&
      (values.currency === "" || values.currency !== defaultCurrency)
    ) {
      setFieldValue("currency", defaultCurrency);
    }
  }, [defaultCurrency, wasUsed, values.currency, setFieldValue]);

  useEffect(() => {
    if (values.reportStartDate || values.reportEndDate) {
      validateForm();
    }
  }, [values.reportStartDate, values.reportEndDate, validateForm]);

  return (
    <FormRootArea>
      <AdminFields />
      <GridContainer component="form" onSubmit={handleSubmit}>
        <PeriodicAcquirerReportResultModal />
        {modalOpen && modalType && (
          <AdminFieldsModal
            type={modalType}
            open={modalOpen}
            onClose={handleCloseModal}
            onSelectISO={handleSelectISO}
          />
        )}
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <TextField
            id="isoID"
            name="isoID"
            fullWidth
            label="ISO ID"
            value={values.isoID}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.isoID}
            touched={touched.isoID}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="open ISO list"
                    onClick={handleOpenModalISO}
                  >
                    <ListIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <CurrencySelectField
            id="currency"
            name="currency"
            fullWidth
            label="Currency"
            value={values.currency}
            required
            maxLength={3}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.currency}
            touched={touched.currency}
            disabled
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportStartDate"
            name="reportStartDate"
            fullWidth
            label="Report Start Date"
            showTimezoneLabel
            value={values.reportStartDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportStartDate}
            touched={touched.reportStartDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridItem xs={12} sm={6} lg={3} padding="15px 15px 15px 0">
          <DateField
            id="reportEndDate"
            name="reportEndDate"
            fullWidth
            label="Report End Date"
            showTimezoneLabel
            value={values.reportEndDate}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldTouched={setFieldTouched}
            required
            error={errors.reportEndDate}
            touched={touched.reportEndDate}
            minDate={moment().subtract(90, "days")}
            maxDate={moment().subtract(1, "day")}
            helperText="Select a date within the last 90 days"
          />
        </GridItem>
        <GridContainer>
          <GridItem xs={12} sm={6} lg={4} padding="15px 15px 15px 0">
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting || status === "loading"}
              loading={isSubmitting || status === "loading"}
              onClick={handleSubmit}
            >
              Generate
            </Button>
          </GridItem>
        </GridContainer>
      </GridContainer>
    </FormRootArea>
  );
};

export default PeriodicAcquirerReport;
